import React, { useState, useEffect, useRef } from 'react';
import {Plus, X, MoreHorizontal, Edit2, Check, ArrowLeft, ArrowRight, Trash2} from 'lucide-react';
import {useTranslation} from "react-i18next";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog';

interface Category {
    id: number;
    name: string;
    color: string;
    textColor: string;
}

interface CategoryItem {
    id: number;
    text: string;
}

interface CategoriesBoardProps {
    config?: {
        categories?: Category[];
        className?: string;
    };
    userData?: any;
    content?: { key: string };
    readonly?: boolean;
    onChange?: (key: string, data: {
        categories: Category[];
        categoryItems: Record<number, CategoryItem[]>;
        categoryNotes: Record<number, string>;
    }) => void;
}

const pastelColors = [
    { bg: 'bg-pink-200', text: 'text-pink-800' },
    { bg: 'bg-purple-200', text: 'text-purple-800' },
    { bg: 'bg-primary/20', text: 'text-primary' },
    { bg: 'bg-green-200', text: 'text-green-800' },
    { bg: 'bg-yellow-200', text: 'text-yellow-800' },
    { bg: 'bg-indigo-200', text: 'text-indigo-800' },
    { bg: 'bg-teal-200', text: 'text-teal-800' },
    { bg: 'bg-rose-200', text: 'text-rose-800' },
];

function moveArrayItem<T>(array: T[], from: number, to: number): T[] {
    if (from === to) return array;
    const arr = [...array];
    const item = arr.splice(from, 1)[0];
    arr.splice(to, 0, item);
    return arr;
}

const CategoriesBoard: React.FC<CategoriesBoardProps> = ({
                                                             config = {},
                                                             userData = {},
                                                             content,
                                                             readonly = false,
                                                             onChange,
                                                         }) => {
    // Get saved data — support both keyed (activity runner) and direct (meeting adapter) patterns
    const keyedData = content?.key ? userData[content.key] : undefined;
    const savedData = keyedData || (userData?.categories || userData?.categoryItems || userData?.categoryNotes ? userData : {});

    // Combine predefined config categories with user-saved categories
    // Priority: saved categories first, then config categories (if not already in saved)
    const initialCategoryList: Category[] = [
        ...(savedData.categories ?? []),
        ...((config.categories ?? []).filter(
            c => !(savedData.categories ?? []).some((u: { id: number; }) => u.id === c.id)
        )),
    ];

    const [categories, setCategories] = useState<Category[]>(initialCategoryList);
    const [categoryItems, setCategoryItems] = useState<Record<number, CategoryItem[]>>(savedData.categoryItems ?? {});
    const [categoryNotes, setCategoryNotes] = useState<Record<number, string>>(savedData.categoryNotes ?? {});
    const [newItemText, setNewItemText] = useState<Record<number, string>>({});
    const [showAddCategory, setShowAddCategory] = useState(false);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [addingBoardIndex, setAddingBoardIndex] = useState<number | null>(null); // For skeleton add
    const [deleteBoardId, setDeleteBoardId] = useState<number | null>(null);

    // Drag and drop state
    const [draggedItem, setDraggedItem] = useState<{categoryId: number, itemId: number, itemIndex: number} | null>(null);
    const [dragOverBoard, setDragOverBoard] = useState<number | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

    // Edit state
    const [editingItem, setEditingItem] = useState<{categoryId: number, itemId: number} | null>(null);
    const [editText, setEditText] = useState('');

    const {t, i18n} = useTranslation();

    // Track last local change to distinguish local vs remote updates
    const lastLocalDataRef = useRef<string>("");
    const isExternalUpdateRef = useRef(false);

    // Sync from external data (e.g., real-time meeting updates from another participant)
    useEffect(() => {
        if (!userData || typeof userData !== 'object') return;
        const keyedExt = content?.key ? userData[content.key] : undefined;
        const ext = keyedExt || (userData?.categories || userData?.categoryItems || userData?.categoryNotes ? userData : null);
        if (!ext) return;

        const externalStr = JSON.stringify({ categories: ext.categories, categoryItems: ext.categoryItems, categoryNotes: ext.categoryNotes });
        if (externalStr === lastLocalDataRef.current) return;

        isExternalUpdateRef.current = true;
        if (ext.categories) setCategories(ext.categories);
        if (ext.categoryItems) setCategoryItems(ext.categoryItems);
        if (ext.categoryNotes) setCategoryNotes(ext.categoryNotes);
    }, [userData, content?.key]);

    // Auto-save userData whenever categories, items, or notes change
    useEffect(() => {
        if (isExternalUpdateRef.current) {
            isExternalUpdateRef.current = false;
            lastLocalDataRef.current = JSON.stringify({ categories, categoryItems, categoryNotes });
            return;
        }
        if (!readonly && onChange && content?.key) {
            lastLocalDataRef.current = JSON.stringify({ categories, categoryItems, categoryNotes });
            onChange(content.key, {
                categories,
                categoryItems,
                categoryNotes
            });
        }
    }, [categories, categoryItems, categoryNotes]);

    const callOnChange = (updatedCategories = categories, updatedItems = categoryItems, updatedNotes = categoryNotes) => {
        // Deprecated: keeping for immediate updates, but useEffect handles persistence
        if (onChange && content?.key) {
            onChange(content.key, {
                categories: updatedCategories,
                categoryItems: updatedItems,
                categoryNotes: updatedNotes
            });
        }
    };

    // Board logic
    const addCategoryItem = (categoryId: number) => {
        const itemText = newItemText[categoryId];
        if (!itemText || !itemText.trim()) return;
        const updatedItems = {
            ...categoryItems,
            [categoryId]: [...(categoryItems[categoryId] || []), {
                id: Date.now(),
                text: itemText.trim()
            }]
        };
        setCategoryItems(updatedItems);
        setNewItemText(prev => ({
            ...prev,
            [categoryId]: ''
        }));
        callOnChange(categories, updatedItems, categoryNotes);
    };

    const removeCategoryItem = (categoryId: number, itemId: number) => {
        const updatedItems = {
            ...categoryItems,
            [categoryId]: categoryItems[categoryId]?.filter(item => item.id !== itemId) || []
        };
        setCategoryItems(updatedItems);
        callOnChange(categories, updatedItems, categoryNotes);
    };

    const moveCategoryItem = (categoryId: number, fromIdx: number, toIdx: number) => {
        const items = categoryItems[categoryId] || [];
        if (fromIdx < 0 || toIdx < 0 || fromIdx >= items.length || toIdx >= items.length) return;
        const updatedItemsArr = moveArrayItem(items, fromIdx, toIdx);
        const updatedItems = { ...categoryItems, [categoryId]: updatedItemsArr };
        setCategoryItems(updatedItems);
        callOnChange(categories, updatedItems, categoryNotes);
    };

    const updateNewItemText = (categoryId: number, text: string) => {
        setNewItemText(prev => ({
            ...prev,
            [categoryId]: text
        }));
    };

    const updateCategoryNote = (categoryId: number, note: string) => {
        const updatedNotes = {
            ...categoryNotes,
            [categoryId]: note
        };
        setCategoryNotes(updatedNotes);
        callOnChange(categories, categoryItems, updatedNotes);
    };

    // Edit item functions
    const startEditingItem = (categoryId: number, itemId: number, currentText: string) => {
        setEditingItem({categoryId, itemId});
        setEditText(currentText);
    };

    const saveEditedItem = () => {
        if (!editingItem || !editText.trim()) return;

        const updatedItems = {
            ...categoryItems,
            [editingItem.categoryId]: categoryItems[editingItem.categoryId]?.map(item =>
                item.id === editingItem.itemId
                    ? { ...item, text: editText.trim() }
                    : item
            ) || []
        };

        setCategoryItems(updatedItems);
        setEditingItem(null);
        setEditText('');
        callOnChange(categories, updatedItems, categoryNotes);
    };

    const cancelEditing = () => {
        setEditingItem(null);
        setEditText('');
    };

    // Drag and drop functions
    const handleDragStart = (e: React.DragEvent, categoryId: number, itemId: number, itemIndex: number) => {
        setDraggedItem({categoryId, itemId, itemIndex});
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e: React.DragEvent, categoryId: number, itemIndex?: number) => {
        e.preventDefault();
        e.stopPropagation(); // Prevent event bubbling to parent
        e.dataTransfer.dropEffect = 'move';
        setDragOverBoard(categoryId);
        setDragOverIndex(itemIndex ?? null);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        // Only clear if we're leaving the entire drop zone
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setDragOverBoard(null);
            setDragOverIndex(null);
        }
    };

    const handleDrop = (e: React.DragEvent, targetCategoryId: number, targetIndex?: number) => {
        e.preventDefault();
        e.stopPropagation(); // Prevent event bubbling to parent

        if (!draggedItem) return;

        const { categoryId: sourceCategoryId, itemId, itemIndex: sourceIndex } = draggedItem;

        // If dropping on itself, do nothing
        if (sourceCategoryId === targetCategoryId && sourceIndex === targetIndex) {
            setDraggedItem(null);
            setDragOverBoard(null);
            setDragOverIndex(null);
            return;
        }

        // Get the item being moved
        const sourceItems = categoryItems[sourceCategoryId] || [];
        const itemToMove = sourceItems.find(item => item.id === itemId);

        if (!itemToMove) return;

        let updatedItems;

        if (sourceCategoryId === targetCategoryId) {
            // Moving within same category - use moveArrayItem
            const items = [...sourceItems];
            let finalTargetIndex = targetIndex ?? items.length;

            // Adjust target index if dragging from above
            if (sourceIndex < finalTargetIndex) {
                finalTargetIndex--;
            }

            const reorderedItems = moveArrayItem(items, sourceIndex, finalTargetIndex);

            updatedItems = {
                ...categoryItems,
                [sourceCategoryId]: reorderedItems
            };
        } else {
            // Moving to different category
            const updatedSourceItems = sourceItems.filter(item => item.id !== itemId);
            const targetItems = categoryItems[targetCategoryId] || [];
            const insertIndex = targetIndex ?? targetItems.length;

            const updatedTargetItems = [
                ...targetItems.slice(0, insertIndex),
                itemToMove,
                ...targetItems.slice(insertIndex)
            ];

            updatedItems = {
                ...categoryItems,
                [sourceCategoryId]: updatedSourceItems,
                [targetCategoryId]: updatedTargetItems
            };
        }

        setCategoryItems(updatedItems);
        callOnChange(categories, updatedItems, categoryNotes);

        // Clear drag state
        setDraggedItem(null);
        setDragOverBoard(null);
        setDragOverIndex(null);
    };

    const handleDragEnd = () => {
        setDraggedItem(null);
        setDragOverBoard(null);
        setDragOverIndex(null);
    };

    // Board add/delete/reorder
    const handleAddCategory = (insertIndex?: number) => {
        if (!newCategoryName.trim()) return;
        const colorIndex = categories.length % pastelColors.length;
        const newCategory: Category = {
            id: Date.now(),
            name: newCategoryName.trim(),
            color: pastelColors[colorIndex].bg,
            textColor: pastelColors[colorIndex].text
        };
        let updatedCategories;
        if (typeof insertIndex === 'number') {
            updatedCategories = [
                ...categories.slice(0, insertIndex),
                newCategory,
                ...categories.slice(insertIndex)
            ];
        } else {
            updatedCategories = [...categories, newCategory];
        }
        setCategories(updatedCategories);
        setShowAddCategory(false);
        setAddingBoardIndex(null);
        setNewCategoryName('');
        callOnChange(updatedCategories, categoryItems, categoryNotes);
    };

    const removeCategory = (categoryId: number) => {
        const updatedCategories = categories.filter(c => c.id !== categoryId);
        const updatedItems = { ...categoryItems };
        delete updatedItems[categoryId];
        const updatedNotes = { ...categoryNotes };
        delete updatedNotes[categoryId];
        setCategories(updatedCategories);
        setCategoryItems(updatedItems);
        setCategoryNotes(updatedNotes);
        callOnChange(updatedCategories, updatedItems, updatedNotes);
    };

    const moveBoard = (fromIdx: number, toIdx: number) => {
        if (fromIdx < 0 || toIdx < 0 || fromIdx >= categories.length || toIdx >= categories.length) return;
        const updatedCategories = moveArrayItem(categories, fromIdx, toIdx);
        setCategories(updatedCategories);
        callOnChange(updatedCategories, categoryItems, categoryNotes);
    };

    // Placeholder if no categories
    const showEmptyPlaceholder = categories.length === 0;

    return (
        <>
        <div className={'flex gap-3 overflow-x-auto pb-2 text-xs mt-3 p-3 rounded-xl bg-white border border-gray-200 shadow-[0_2px_8px_rgba(79,70,229,0.08)] ' + (config.className || '')}>
            {showEmptyPlaceholder && (
                <div className="w-full flex flex-col items-center justify-center min-h-[180px] bg-gray-50 rounded-xl p-4 border border-dashed border-gray-300">
                    <div className="text-sm font-semibold text-gray-700 mb-1">No boards yet</div>
                    <div className="text-xs text-gray-500 mb-3">Create your first board to start organizing.</div>
                    {!readonly && (
                        <>
                            {!showAddCategory ? (
                                <button
                                    onClick={() => setShowAddCategory(true)}
                                    className="flex items-center gap-2 px-4 py-2 text-sm bg-primary text-white font-semibold rounded-lg hover:bg-primary/90 transition-all active:scale-[0.97]"
                                >
                                    <Plus className="w-4 h-4" />
                                    Add Board
                                </button>
                            ) : (
                                <div className="flex gap-2 mt-2">
                                    <input
                                        type="text"
                                        value={newCategoryName}
                                        onChange={(e) => setNewCategoryName(e.target.value)}
                                        placeholder="Board name"
                                        className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 shadow-sm"
                                        onKeyDown={(e) => e.key === 'Enter' && handleAddCategory()}
                                    />
                                    <button
                                        onClick={() => handleAddCategory()}
                                        className="px-3 py-1.5 text-sm bg-primary text-white font-semibold rounded-lg hover:bg-primary/90 transition-all active:scale-[0.97]"
                                    >
                                        Add
                                    </button>
                                    <button
                                        onClick={() => {
                                            setShowAddCategory(false);
                                            setNewCategoryName('');
                                        }}
                                        className="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
            {categories.map((category, boardIdx) => {
                const categoryItemsList = categoryItems[category.id] || [];
                const isDragOverThisBoard = dragOverBoard === category.id;

                return (
                    <div
                        key={category.id}
                        className={`flex-shrink-0 w-[220px] bg-white rounded-xl p-3 relative transition-all border shadow-sm ${
                            readonly ? 'disabled pointer-events-none select-none mt-3' : ''
                        } ${isDragOverThisBoard ? 'ring-2 ring-primary/40 border-primary/30 bg-primary/5' : 'border-gray-200'}`}
                        onDragOver={(e) => handleDragOver(e, category.id)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, category.id)}
                    >
                        {/* Board Header */}
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2 min-w-0">
                                <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold truncate max-w-[130px] ${category.color ?? ''} ${category.textColor ?? ''}`} title={category.name}>
                                    {category.name ?? ''}
                                </span>
                                <span className="text-xs font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-full flex-shrink-0">
                                    {categoryItemsList.length}
                                </span>
                            </div>
                            {!readonly && (
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <button className="min-h-[32px] min-w-[32px] flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors flex-shrink-0">
                                            <MoreHorizontal className="w-4 h-4 text-gray-500" />
                                        </button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-44">
                                        <DropdownMenuItem
                                            onClick={() => moveBoard(boardIdx, boardIdx - 1)}
                                            disabled={boardIdx === 0}
                                            className="gap-2"
                                        >
                                            <ArrowLeft className="w-3.5 h-3.5" />
                                            Move left
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                            onClick={() => moveBoard(boardIdx, boardIdx + 1)}
                                            disabled={boardIdx === categories.length - 1}
                                            className="gap-2"
                                        >
                                            <ArrowRight className="w-3.5 h-3.5" />
                                            Move right
                                        </DropdownMenuItem>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem
                                            onClick={() => setDeleteBoardId(category.id)}
                                            className="gap-2 text-destructive focus:text-destructive"
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                            Delete board
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            )}
                        </div>


                        {/* Cards Container */}
                        <div className="space-y-2 min-h-[80px] max-h-64 overflow-y-auto overscroll-contain" >
                            {categoryItemsList.map((item, itemIdx) => {
                                const isBeingDragged = draggedItem?.itemId === item.id;
                                const isEditingThisItem = editingItem?.categoryId === category.id && editingItem?.itemId === item.id;
                                const showDropZone = isDragOverThisBoard && dragOverIndex === itemIdx && draggedItem?.itemId !== item.id;

                                return (
                                    <React.Fragment key={item.id}>
                                        {/* Drop zone above current item */}
                                        {showDropZone && (
                                            <div className="h-2 bg-primary/40 rounded-lg opacity-50 transition-all"></div>
                                        )}

                                        <div
                                            draggable={!readonly && !isEditingThisItem}
                                            onDragStart={(e) => handleDragStart(e, category.id, item.id, itemIdx)}
                                            onDragEnd={handleDragEnd}
                                            onDragOver={(e) => handleDragOver(e, category.id, itemIdx)}
                                            onDrop={(e) => handleDrop(e, category.id, itemIdx)}
                                            className={`bg-white rounded-lg p-2 shadow-sm hover:shadow-sm transition-all border border-gray-200 hover:border-primary/30 group ${
                                                isBeingDragged ? 'opacity-50 rotate-1 scale-105 shadow-md' : 'cursor-move'
                                            } ${!readonly && !isEditingThisItem ? 'hover:scale-102' : ''}`}
                                        >
                                            <div className="flex justify-between items-start">
                                                {isEditingThisItem ? (
                                                    <div className="flex-1 flex gap-2">
                                                        <input
                                                            type="text"
                                                            value={editText}
                                                            onChange={(e) => setEditText(e.target.value)}
                                                            className="flex-1 text-sm text-gray-800 border-none outline-none bg-transparent resize-none"
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') {
                                                                    saveEditedItem();
                                                                } else if (e.key === 'Escape') {
                                                                    cancelEditing();
                                                                }
                                                            }}
                                                            onBlur={saveEditedItem}
                                                            autoFocus
                                                        />
                                                        <button
                                                            onClick={saveEditedItem}
                                                            className="p-1 hover:bg-green-100 rounded text-green-600 transition-colors"
                                                            title="Save changes"
                                                        >
                                                            <Check className="w-3 h-3" />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <p className="text-sm text-gray-800 leading-relaxed">
                                                            {item.text}
                                                        </p>
                                                        {!readonly && (
                                                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                                <button
                                                                    onClick={() => startEditingItem(category.id, item.id, item.text)}
                                                                    className="p-1 hover:bg-primary/10 rounded text-gray-400 hover:text-primary transition-colors"
                                                                    title="Edit card"
                                                                >
                                                                    <Edit2 className="w-3 h-3" />
                                                                </button>
                                                                <button
                                                                    onClick={() => removeCategoryItem(category.id, item.id)}
                                                                    className="p-1 hover:bg-red-100 rounded text-gray-400 hover:text-red-500 transition-colors"
                                                                    title="Delete card"
                                                                >
                                                                    <X className="w-3 h-3" />
                                                                </button>
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </React.Fragment>
                                );
                            })}

                            {/* Drop zone at the end of the list */}
                            {isDragOverThisBoard && dragOverIndex === categoryItemsList.length && (
                                <div className="h-2 bg-primary/40 rounded-lg opacity-50 transition-all"></div>
                            )}

                            {categoryItemsList.length === 0 && (
                                <div
                                    className={`min-h-[80px] flex items-center justify-center rounded-lg border-2 border-dashed transition-all ${
                                        isDragOverThisBoard ? 'border-primary/40 bg-primary/5 text-primary' : 'border-gray-200 text-gray-400'
                                    }`}
                                >
                                    <span className="text-xs font-medium">
                                        {isDragOverThisBoard ? 'Drop card here' : 'No cards yet'}
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Add New Card */}
                        {!readonly &&
                            <div className="space-y-2 mt-3">
                                <div className="flex gap-1.5">
                                    <input
                                        type="text"
                                        value={newItemText[category.id] || ''}
                                        onChange={(e) => updateNewItemText(category.id, e.target.value)}
                                        placeholder="Add a card..."
                                        className="flex-1 px-2.5 py-2 text-sm border border-gray-200 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 bg-white"
                                        onKeyDown={(e) => e.key === 'Enter' && addCategoryItem(category.id)}
                                    />
                                    <button
                                        onClick={() => addCategoryItem(category.id)}
                                        className="min-h-[36px] min-w-[36px] px-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-all active:scale-[0.97] flex items-center justify-center"
                                    >
                                        <Plus className="w-4 h-4" />
                                    </button>
                                </div>

                                {/* Board Notes - Always visible */}
                                <textarea
                                    value={categoryNotes[category.id] || ''}
                                    onChange={(e) => updateCategoryNote(category.id, e.target.value)}
                                    placeholder={`${t('notes') || 'Notes'}...`}
                                    className="w-full mt-1 px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg focus:border-primary focus:ring-1 focus:ring-primary/20 resize-none h-14 placeholder:text-gray-300 transition-all duration-150"
                                />
                            </div>
                        }
                    </div>
                );
            })}
            {/* Add board button at end of scroll row */}
            {!readonly && categories.length > 0 && (
                <div className="flex-shrink-0 flex flex-col items-center justify-start pt-1">
                    {addingBoardIndex !== categories.length ? (
                        <button
                            className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-primary border border-primary/30 bg-primary/5 rounded-lg hover:bg-primary/10 transition-all active:scale-[0.97] whitespace-nowrap"
                            title="Add board"
                            onClick={() => { setAddingBoardIndex(categories.length); setShowAddCategory(false); setNewCategoryName(''); }}
                        >
                            <Plus className="w-3.5 h-3.5" />
                            Add board
                        </button>
                    ) : (
                        <div className="flex flex-col items-start gap-1 w-[140px]">
                            <input
                                type="text"
                                value={newCategoryName}
                                onChange={(e) => setNewCategoryName(e.target.value)}
                                placeholder="Board name"
                                className="w-full px-2.5 py-1.5 border border-gray-200 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 text-xs"
                                onKeyDown={(e) => e.key === 'Enter' && handleAddCategory(categories.length)}
                                autoFocus
                            />
                            <div className="flex gap-1">
                                <button
                                    onClick={() => handleAddCategory(categories.length)}
                                    className="px-2.5 py-1 bg-primary text-white rounded-lg hover:bg-primary/90 transition-all active:scale-[0.97] text-xs font-medium"
                                >
                                    Add
                                </button>
                                <button
                                    onClick={() => { setAddingBoardIndex(null); setNewCategoryName(''); }}
                                    className="px-2.5 py-1 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-xs"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>

        {/* Delete board confirmation */}

        <AlertDialog open={deleteBoardId !== null} onOpenChange={(open) => !open && setDeleteBoardId(null)}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>
                        Delete "{categories.find(c => c.id === deleteBoardId)?.name}"?
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                        This will delete the board and all {deleteBoardId !== null ? (categoryItems[deleteBoardId]?.length ?? 0) : 0} cards in it. This cannot be undone.
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                        onClick={() => {
                            if (deleteBoardId !== null) {
                                removeCategory(deleteBoardId);
                                setDeleteBoardId(null);
                            }
                        }}
                        className="bg-destructive text-white hover:bg-destructive/90"
                    >
                        Delete
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
        </>
    );
};

export default CategoriesBoard;